import { useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  useMoveToCategory,
  type CompanyCategory,
  type MoveToCategoryCompany,
} from "@/hooks/useMoveToCategory";

interface MoveToDropdownProps {
  company: MoveToCategoryCompany;
  /**
   * "button" (default): renders a standalone outlined button labeled "Move to"
   * with the ArrowRightLeft icon. Use in Quick Actions rows.
   *
   * "items": renders DropdownMenuItem children only, meant to be spread inside
   * an existing parent DropdownMenu (e.g. a ⋮ More menu on the Prospect page).
   * In "items" mode the caller is responsible for providing the parent dropdown.
   */
  variant?: "button" | "items";
}

/**
 * Shared Move To category UI. Handles four states:
 *   1. Single-category M&A → dropdown with counterpart opening the Move/Also dialog
 *   2. Single-category non-M&A → standard dropdown of other categories
 *   3. Dual-listed M&A → dropdown with "Remove from {counterpart}" instead of counterpart
 *   4. Click-to-add-dual for M&A primaries (via AlertDialog)
 */
export function useMoveTo(company: MoveToCategoryCompany) {
  const {
    moveToCategory,
    addDualRole,
    removeDualRole,
    otherCategories,
    isDualListed,
    counterpartCategory,
  } = useMoveToCategory(company);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<CompanyCategory | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const isMACompany = company.category === "Buyer Network" || company.category === "Capital Providers";

  // Handler for clicking a normal target category. If the target is the M&A
  // counterpart AND we are not already dual-listed, we open the AlertDialog
  // (which offers Move OR Also). Otherwise it queues a plain exclusive
  // migration through the ConfirmDialog below.
  const handleCategoryClick = (target: CompanyCategory) => {
    if (isMACompany && !isDualListed && target === counterpartCategory) {
      setDialogOpen(true);
      return;
    }
    setPendingMove(target);
  };

  // Dual-listed → picking a non-counterpart category drops the counterpart
  // listing. We surface that in the confirmation description so the user
  // isn't surprised.
  const pendingMoveDropsCounterpart = Boolean(
    pendingMove && isDualListed && counterpartCategory &&
    pendingMove !== counterpartCategory && pendingMove !== company.category
  );

  // The dropdown items — reusable for both "button" and "items" variant.
  const items = (
    <>
      {otherCategories.map(cat => (
        <DropdownMenuItem key={cat} onClick={() => handleCategoryClick(cat)}>
          {cat}
        </DropdownMenuItem>
      ))}
      {/* When dual-listed, show the "Remove from {counterpart}" option instead of counterpart in otherCategories */}
      {isDualListed && counterpartCategory && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={removeDualRole}>
            Remove from {counterpartCategory}
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  const dialog = (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Add {company.name} to {counterpartCategory}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {company.name} is currently in {company.category}. How should it appear in {counterpartCategory}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            variant="outline"
            onClick={async () => {
              setDialogOpen(false);
              await addDualRole();
            }}
          >
            Also list here (keep in both)
          </Button>
          <AlertDialogAction
            onClick={async () => {
              setDialogOpen(false);
              if (counterpartCategory) await moveToCategory(counterpartCategory);
            }}
          >
            Move here (remove from {company.category})
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const moveConfirm = (
    <ConfirmDialog
      open={!!pendingMove}
      onOpenChange={(next) => !next && !isMoving && setPendingMove(null)}
      title={pendingMove ? `Move ${company.name} to ${pendingMove}?` : ""}
      description={
        pendingMoveDropsCounterpart
          ? `This changes the primary category from ${company.category} to ${pendingMove}. ${company.name} will also be removed from the ${counterpartCategory} list (dual-listing dropped). The page will reload to show the ${pendingMove} detail view.`
          : `This changes the primary category from ${company.category} to ${pendingMove}. The page will reload to show the ${pendingMove} detail view.`
      }
      confirmLabel={`Move to ${pendingMove ?? ""}`}
      cancelLabel="Cancel"
      variant="default"
      isLoading={isMoving}
      onConfirm={async () => {
        if (!pendingMove) return;
        setIsMoving(true);
        try {
          await moveToCategory(pendingMove);
        } finally {
          setIsMoving(false);
        }
      }}
    />
  );

  return {
    items,
    dialogs: (
      <>
        {dialog}
        {moveConfirm}
      </>
    ),
  };
}

export function MoveToDropdown({ company, variant = "button" }: MoveToDropdownProps) {
  const { items, dialogs } = useMoveTo(company);

  // "items" mode returns only the menu items: dialogs rendered inside a menu
  // unmount when it closes. Callers that own the menu use useMoveTo and render
  // the dialogs beside it.
  if (variant === "items") return <>{items}</>;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5">
            <ArrowRightLeft className="w-3 h-3" /> Move to
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {items}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </>
  );
}
